import { Fragment, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, Checkbox, Stack, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import Informed_Consent from "../Trials/Informed_Consent";
import TargetLesionImprovementAssessment from "../Trials/TargetLesionImprovementAssessment";
import Photographic_App_Before_Injection from "../Trials/Photographic_App_Before_Injection";
import Photographic_App_After_Injection from "../Trials/Photographic_App_After_Injection";
import Participant_Assessments_Pre_Injection from "../Trials/Participant_Assessments_Pre_Injection";
import Target_Lesion_Injection_Pain from "../Trials/Target_Lesion_Injection_Pain";
import Concomitant_Medication from "../Trials/Concomitant_Medication";
import Adverse_Event from "../Trials/Adverse_Event";
import Medical_surgical from "../Trials/Medical_Surgical_Worksheet";
import Informed_Consent_Stem_Cell from "../Trials/Informed_Consent_Stem_Cell";
import Demographics_Information_Stem_Cell from "../Trials/Demographics_Information_Stem_Cell";
import Inclusion_Criteria_Stem_Cell from "../Trials/Inclusion_Criteria_Stem_Cell";
import Exclusion_Criteria_Stem_Cell from "../Trials/Exclusion_Criteria_Stem_Cell";
import Skin_Biopsy_Stem_Cell from "../Trials/Skin_Biopsy_Stem_Cell";
import Visit_Comments_Stem_Cell from "../Trials/Visit_Comments_Stem_Cell";
import Sutures_Removed_Stem_Cell from "../Trials/Sutures_Removed_Stem_Cell";
import Fitzpatrick_Skin_Type from "../Trials/Fitzpatrick_Skin_Type";
import Global_Severity_Score_General from "../Trials/Global_Severity_Score_General";
import Child_Bearing_Potential from "../Trials/Child_Bearing_Potential";
import PhotoGraphic_App from "../Trials/PhotoGraphic_App";
import Dermatologic_Assessment from "../Trials/Dermatologic_Assessment";
import Exit_Form from "../Trials/Exit_Form";
import Participant_Contact_Info from "../Trials/ParticpantContactInfo";
import Site_Patient_Identification from "../Trials/SitePatientIdentification";
import Birth_Control from "../Trials/Birth_Control";
import Demographics from "../Trials/Demographics";
import Target_Lesion_Identification from "../Trials/Target_Lesion_Identification_Form";
import Inclusion_Criteria from "../Trials/Inclusion_Criteria_Form";
import Exclusion_Criteria from "../Trials/Exclusion_Criteria";
import Global_Severity_Score from "../Trials/Global_Severity_Score";
import Target_Lesion_Erythema_Assessment from "../Trials/Target_Lesion_Erythema_Assessment";
import Target_Lesion_Severity_Assessment from "../Trials/Target_Lesion_Severity_Assessment";
import Target_Lesion_Photography_Pre_Injection from "../Trials/Target_Lesion_Photography_Pre_Injection";
import Target_Lesion_Videography from "../Trials/Target_Lesion_Videography";
import Intralesional_Injection from "../Trials/Intralesional_Injections";
import Target_Lesion_Photography_Post_Injection from "../Trials/Target_Lesion_Photography_Post_Injection";
import Reminders from "../Trials/Reminders";
import PARTICIPANT_ASSESSMENTS from "../Trials/PARTICIPANT_ASSESSMENTS";
import Medical_History_Concomitant_Medication_Treatment from "../Trials/Medical History & Concomitant Medication & Treatment";

import EcrfBlocks from "../Trials/EcrfBlocks";
import CustomButton from "../UI/CustomButton";
import AddTrialTypePopUp from "../PopUp/AddTrialtypePopUp";
import MainAccordion from "../Accordion/MainAccordion";
import logUserAction from "../../apis/userLogger";
import { useEcrfContext } from "../../context/EcrfContext";
import useVisitAssessmentAreas from "../../hooks/useVisitAssessmentAreas";
import { USER_ROLES } from "../../utils/constants";
import useGetSinglePatient from "../../hooks/useGetSinglePatient";
import BlockList from "./BlocksList";
import SurveysModule from "./SurveysModule";
import AdvancedSurveysModule from "./AdvancedSurveysModule";
import VideosModule from "./VideosModule";
import PhotosModule from "./PhotosModule";
import AIAssessmentModule from "./AIAssessmentModule";
import InstructionsModule from "./InstructionsModule";
import ExternalMediaVisitConfig from "./ExternalMediaVisitConfig";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import { isTrialConfigFinalized } from "../../utils/helpers";
import { getVisibleSteps } from "../../utils/visitSteps";

const AllBlocksAccordion = ({
  selectedBlock,
  setSelectedBlock,
  handleSelectBlock,
  allSurveys,
  setMessage,
  setTypeOfMessage,
  modalVisitChangeShow,
  setModalVisitChangeShow,
  newForm,
  setNewForm,
  searchTrailType,
  setSearchTrailType,
  searchBlockName,
  setSearchBlockName,
  handleVisitUpdate,
  setHasUnsavedEcrfChanges,
}) => {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const queryClient = useQueryClient();

  const {
    selectedVisit,
    setSelectedVisit,
    patientId,
    trial,
    selectedSessionId,
    hasChanged,
    setHasChanged,
    photoTypes,
    localBlocks,
    surveyData,
    setSurveyData,
    hasEcrfBlocksChanged,
    setHasEcrfBlocksChanged,
    isAllEcrfBlocks,
    setIsAllEcrfBlocks,
  } = useEcrfContext();
  // Per-patient edits stay open on a finalized Live trial; visit configuration does not
  const isTrialLocked =
    ["Completed", "Archived"].includes(trial?.status) ||
    (isTrialConfigFinalized(trial) && !patientId);

  const visibleSteps = getVisibleSteps(trial);

  const { trialFilter } = useOutletContext() ?? {};

  const refetchVisits = () => {
    queryClient.invalidateQueries({ queryKey: ["visits"] });
  };

  const { data: infoData } = useGetSinglePatient({
    patientId,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const [showTrialTypeModal, setShowTrialTypeModal] = useState(false);
  const [blockId, setBlockId] = useState("");
  const [trialTypeIds, setTrialTypeIds] = useState([]);
  const [expandedTrialTypes, setExpandedTrialTypes] = useState({});

  const { data: assessmentAreas } = useVisitAssessmentAreas({
    visitId: patientId ? selectedVisit?.id : null,
    trialId: patientId ? null : trial?.id,
  });

  const handleTrialTypeDisplay = (typesName) => {
    setExpandedTrialTypes((prev) => ({
      ...prev,
      [typesName]: !prev[typesName],
    }));
  };

  const areBlocksSame = (selectedBlock, block) => {
    function areTrialTypesEqual(blockA, blockB) {
      const a = blockA?.trial_type
        ?.map((t) => {
          let value = [];
          if ("label" in t) {
            value = t.label;
          } else if ("title" in t) {
            value = t.title;
          }
          return value;
        })
        .sort();
      const b = blockB?.trial_type
        ?.map((t) => {
          let value = [];
          if ("label" in t) {
            value = t.label;
          } else if ("title" in t) {
            value = t.title;
          }
          return value;
        })
        .sort();

      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;

      return a.every((val, index) => val === b[index]);
    }

    return (
      selectedBlock?.name === block.name &&
      selectedBlock?.name === block.name &&
      selectedBlock?.new_block === block?.new_block &&
      areTrialTypesEqual(selectedBlock, block)
    );
  };

  const handleBlockChange = (block) => {
    setHasEcrfBlocksChanged(true);
    if (surveyData?.some((b) => areBlocksSame(block, b))) {
      const newBlocks = surveyData?.filter((b) => b.name !== block.name);

      setSurveyData(newBlocks);
    } else {
      const newBlocks = [...surveyData, block];

      setSurveyData(newBlocks);
    }
  };

  function areTrialTypesEqual(blockA, blockB) {
    const a = blockA?.trial_type
      ?.map((t) => {
        let value = [];
        if ("label" in t) {
          value = t.label;
        } else if ("title" in t) {
          value = t.title;
        }
        return value;
      })
      .sort();
    const b = blockB?.trial_type
      ?.map((t) => {
        let value = [];
        if ("label" in t) {
          value = t.label;
        } else if ("title" in t) {
          value = t.title;
        }
        return value;
      })
      .sort();

    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;

    return a.every((val, index) => val === b[index]);
  }

  return (
    <>
      <MainAccordion
        title="Trial Blocks"
        isOpen={true}
        data={
          <Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <Stack
                sx={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  flex: 1,
                }}
              >
                {(role === "CRO Admin" ||
                  role === "Cro_coordinator" ||
                  role === "Data Manager" ||
                  role === "Super Admin") &&
                  patientId === undefined && (
                    <Box sx={{ px: 1, pb: 1 }}>
                      <Typography variant="subtitle2" sx={{ my: 1 }}>
                        Select eCRF blocks to build Source Document
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        name="trial_type"
                        value={searchTrailType}
                        onChange={(e) => setSearchTrailType(e.target.value)}
                        placeholder="Search by trial type"
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        name="trial_name"
                        value={searchBlockName}
                        onChange={(e) => setSearchBlockName(e.target.value)}
                        placeholder="Search by block name"
                      />
                    </Box>
                  )}

                {role !== "Reviewer" && (
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      p: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: "pointer",
                        color:
                          selectedBlock === "Patient Activity in this Visit"
                            ? "text.primary"
                            : "primary.main",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => {
                        setSelectedBlock("Patient Activity in this Visit");
                        setNewForm(false);
                      }}
                    >
                      Patient Activity in this Visit
                    </Typography>
                  </Stack>
                )}

                {!patientId &&
                  !isTrialLocked &&
                  canSaveForFollowingVisits(selectedVisit) &&
                  (role === USER_ROLES.CRO ||
                    role === USER_ROLES.CRO_COORDINATOR ||
                    role === USER_ROLES.DATA_MANAGER) && (
                    <Box sx={{ p: 1 }}>
                      <SaveForFollowingVisitsCheckbox
                        label="Save eCRF blocks for all coming visits"
                        checked={isAllEcrfBlocks}
                        onChange={() => {
                          setIsAllEcrfBlocks((prev) => !prev);
                          setHasEcrfBlocksChanged(true);
                        }}
                      />
                    </Box>
                  )}

                {surveyData?.length > 0 && (
                  <BlockList
                    surveyData={surveyData}
                    setSurveyData={setSurveyData}
                    selectedBlock={selectedBlock}
                    role={role}
                    patientId={patientId}
                    expandedTrialTypes={expandedTrialTypes}
                    handleTrialTypeDisplay={handleTrialTypeDisplay}
                    handleSelectBlock={handleSelectBlock}
                    handleBlockChange={handleBlockChange}
                    areBlocksSame={areBlocksSame}
                    areTrialTypesEqual={areTrialTypesEqual}
                    logUserAction={logUserAction}
                    infoData={infoData}
                    trial={trial}
                    setHasEcrfBlocksChanged={setHasEcrfBlocksChanged}
                    selectedVisit={selectedVisit}
                  />
                )}

                <AddTrialTypePopUp
                  showTrialTypeModal={showTrialTypeModal}
                  setShowTrialTypeModal={setShowTrialTypeModal}
                  blockId={blockId}
                  trialTypeIds={trialTypeIds}
                  setTrialTypeIds={setTrialTypeIds}
                />

                {(role === "CRO Admin" || role === "Cro_coordinator" || role === "Data Manager") &&
                  !patientId &&
                  localBlocks?.map((block) => {
                    if (
                      !surveyData?.some(
                        (selectedBlock) =>
                          selectedBlock?.name === block.name &&
                          selectedBlock?.new_block === block?.new_block &&
                          areTrialTypesEqual(selectedBlock, block)
                      )
                    ) {
                      return (
                        <Stack
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            p: 1,
                            cursor: "pointer",
                            color: selectedBlock === block.name ? "text.primary" : "primary.main",
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography
                              variant="body2"
                              onClick={() => handleSelectBlock(block.name, block.new_block)}
                            >
                              {block.name}
                            </Typography>

                            {patientId === undefined &&
                              role !== "Investigator" &&
                              role !== "Inv_coordinator" &&
                              role !== "Reviewer" && (
                                <Checkbox
                                  checked={surveyData?.some(
                                    (selectedBlock) =>
                                      selectedBlock?.name === block.name &&
                                      selectedBlock?.new_block === block?.new_block &&
                                      areTrialTypesEqual(selectedBlock, block)
                                  )}
                                  name={block.name}
                                  onChange={() => handleBlockChange(block)}
                                  size="small"
                                  sx={{ p: 0 }}
                                />
                              )}
                          </Stack>

                          <Stack
                            direction="row"
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            gap={1}
                          >
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{ color: "text.primary" }}
                            >
                              [
                              {block.trial_type?.map((blockObject, index) => {
                                if (index < 2 || expandedTrialTypes[block.name]) {
                                  return (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      key={index}
                                      onClick={() => handleTrialTypeDisplay(block.name)}
                                      sx={{ cursor: "pointer" }}
                                    >
                                      {blockObject.title || blockObject.label}
                                      {index < block.trial_type.length - 1 && ", "}
                                    </Typography>
                                  );
                                }
                                return null;
                              })}
                              {block.trial_type.length > 2 && !expandedTrialTypes[block.name] && (
                                <Typography
                                  component="span"
                                  onClick={() => handleTrialTypeDisplay(block.name)}
                                  sx={{
                                    cursor: "pointer",
                                    color: "primary.main",
                                  }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </Typography>
                              )}
                              ]
                            </Typography>

                            <Typography variant="caption" component="div">
                              [
                              {block.roles &&
                                block.roles?.map((rolesObject, index) => {
                                  let name;
                                  if (index === block.roles.length - 1) name = rolesObject.name;
                                  else name = `${rolesObject.name}, `;
                                  return (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      key={index}
                                      sx={{
                                        color:
                                          rolesObject.name === "CRC"
                                            ? "error.main"
                                            : rolesObject.name === "INV"
                                              ? "warning.main"
                                              : "text.primary",
                                      }}
                                    >
                                      {name}
                                    </Typography>
                                  );
                                })}
                              ]
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    } else {
                      return null;
                    }
                  })}
              </Stack>

              <Stack sx={{ maxHeight: "70vh", overflowY: "auto", flex: 4, px: 1 }}>
                <>
                  {selectedBlock === "Patient Activity in this Visit" ? (
                    selectedVisit?.visit_number !== -4 ? (
                      <Stack
                        gap={1}
                        sx={{
                          width: "100%",
                        }}
                      >
                        {!patientId &&
                          (role === USER_ROLES.CRO ||
                            role === USER_ROLES.CRO_COORDINATOR ||
                            role === USER_ROLES.DATA_MANAGER) && (
                            <CustomButton
                              size="small"
                              data-testid="update-visit-info-btn"
                              sx={{
                                width: "20%",
                                marginLeft: "auto",
                              }}
                              onClick={() => handleVisitUpdate()}
                              disabled={!hasChanged && !hasEcrfBlocksChanged}
                            >
                              Update Visit Info
                            </CustomButton>
                          )}

                        {selectedVisit && selectedVisit?.id && (
                          <Fragment key={selectedVisit?.id}>
                            {visibleSteps.map((step) => {
                              const key = step.id ?? `${step.module}-${step.position ?? 0}`;
                              const commonProps = {
                                key,
                                step,
                                role,
                                selectedSessionId,
                                selectedVisit,
                                patientId,
                                visitId: selectedVisit?.id,
                                isTrialLocked,
                              };

                              switch (step.module) {
                                case "POEM":
                                  return <SurveysModule {...commonProps} allSurveys={allSurveys} />;
                                case "Advanced Surveys":
                                  return <AdvancedSurveysModule {...commonProps} />;
                                case "Videos":
                                  return <VideosModule {...commonProps} />;
                                case "Photos":
                                  return <PhotosModule {...commonProps} photoTypes={photoTypes} />;
                                case "AI Assessment":
                                  return (
                                    <AIAssessmentModule
                                      {...commonProps}
                                      trial={trial}
                                      assessmentAreas={assessmentAreas}
                                    />
                                  );
                                case "Instructions":
                                  return <InstructionsModule {...commonProps} trial={trial} />;
                                default:
                                  return null;
                              }
                            })}

                            {!patientId && (
                              <ExternalMediaVisitConfig
                                visit={selectedVisit}
                                visitId={selectedVisit?.id}
                                trial={trial}
                                isTrialLocked={isTrialLocked}
                              />
                            )}
                          </Fragment>
                        )}
                      </Stack>
                    ) : (
                      <> No Activity for this Visit </>
                    )
                  ) : (
                    <>
                      {newForm ? (
                        <EcrfBlocks
                          visit_number={selectedVisit?.visit_number}
                          visitName={selectedVisit?.visit_name}
                          visitId={selectedVisit?.id}
                          photo_session={selectedVisit?.photo_session}
                          selectedBlock={selectedBlock}
                          setMessage={setMessage}
                          newForm={newForm}
                          setTypeOfMessage={setTypeOfMessage}
                          setBlockId={setBlockId}
                          patientId={patientId}
                          trialId={trial?.id}
                          trialName={trial?.name}
                          allowCopyingFromOtherVisitsEcrfs={trial?.allow_copying_other_visits_ecrfs}
                          selectedSessionId={selectedSessionId}
                          photoSessionId={selectedVisit?.photo_session_id}
                          setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
                          show_incomplete_date_warning_on_ecrf={
                            trial?.show_incomplete_date_warning_on_ecrf
                          }
                          isTrialLocked={isTrialLocked}
                        />
                      ) : (
                        <>
                          {selectedBlock === "Participant Contact Information" && (
                            <Participant_Contact_Info
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                            />
                          )}
                          {selectedBlock === "Site & Patient Identification" && (
                            <Site_Patient_Identification
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Demographics" && (
                            <Demographics
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Informed Consent" && (
                            <Informed_Consent
                              patient_id={patientId}
                              trialId={trial.id}
                              visit_number={selectedVisit?.visit_number}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Birth Control UPT" && (
                            <Birth_Control
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              setSelectedVisit={setSelectedVisit}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Inclusion Criteria" && (
                            <Inclusion_Criteria
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              selectedSessionId={selectedSessionId}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Exclusion Criteria" && (
                            <Exclusion_Criteria
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Erythema Assessment" && (
                            <Target_Lesion_Erythema_Assessment
                              selectedVisit={selectedVisit}
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                            />
                          )}
                          {selectedBlock === "Global Severity Score" && (
                            <Global_Severity_Score
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Severity Assessment" && (
                            <Target_Lesion_Severity_Assessment
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                            />
                          )}
                          {selectedBlock === "Intralesional Injections" && (
                            <Intralesional_Injection
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Participant Assessments" && (
                            <PARTICIPANT_ASSESSMENTS
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Identification" && (
                            <Target_Lesion_Identification
                              patient_id={patientId}
                              trialId={trial.id}
                              visit_number={selectedVisit?.visit_number}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Reminders" && (
                            <Reminders
                              setSelectedVisit={setSelectedVisit}
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Improvement Assessment" && (
                            <TargetLesionImprovementAssessment
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                            />
                          )}
                          {selectedBlock === "Target Lesion Videography" && (
                            <Target_Lesion_Videography
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Photography - PRE-Injection" && (
                            <Target_Lesion_Photography_Pre_Injection
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Photography - POST-Injection" && (
                            <Target_Lesion_Photography_Post_Injection
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              selectedSessionId={selectedSessionId}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Medical History Concomitant Medication Treatment" && (
                            <Medical_History_Concomitant_Medication_Treatment
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              selectedSessionId={selectedSessionId}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Photographic App (Before) Injection" && (
                            <Photographic_App_Before_Injection
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setSelectedVisit={setSelectedVisit}
                              selectedSessionId={selectedSessionId}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Photographic App (After) Injection" && (
                            <Photographic_App_After_Injection
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              selectedSessionId={selectedSessionId}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Participant Assessments - Pre-Injection" && (
                            <Participant_Assessments_Pre_Injection
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Target Lesion Injection Pain" && (
                            <Target_Lesion_Injection_Pain
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              extra_photo={selectedVisit?.extra_photos}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              selectedSessionId={selectedSessionId}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                            />
                          )}
                          {selectedBlock === "Concomitant Medication/Treatment Work Sheet" && (
                            <Concomitant_Medication
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setSelectedVisit={setSelectedVisit}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Adverse Event Log" && (
                            <Adverse_Event
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              setSelectedVisit={setSelectedVisit}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Medical History Work Sheet" && (
                            <Medical_surgical
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              setSelectedVisit={setSelectedVisit}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}

                          {selectedBlock === "Informed Consent (Stem Cell)" && (
                            <Informed_Consent_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              selectedSessionId={selectedSessionId}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Demographic Information (Stem Cell)" && (
                            <Demographics_Information_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Inclusion Criteria (Stem Cell)" && (
                            <Inclusion_Criteria_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Exclusion Criteria (Stem Cell)" && (
                            <Exclusion_Criteria_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Skin Biopsy (Stem Cell)" && (
                            <Skin_Biopsy_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Visit Comments (Stem Cell)" && (
                            <Visit_Comments_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Suture_removal" && (
                            <Sutures_Removed_Stem_Cell
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Fitzpatrick Skin Type" && (
                            <Fitzpatrick_Skin_Type
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Global Severity Score General" && (
                            <Global_Severity_Score_General
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Child Bearing Potential" && (
                            <Child_Bearing_Potential
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "PhotoGraphic App" && (
                            <PhotoGraphic_App
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "Dermatologic Assessment" && (
                            <Dermatologic_Assessment
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                          {selectedBlock === "EXIT FORM" && (
                            <Exit_Form
                              patient_id={patientId}
                              visit_number={selectedVisit?.visit_number}
                              trialId={trial.id}
                              photo_session={selectedVisit?.photo_session}
                              setMessage={setMessage}
                              setTypeOfMessage={setTypeOfMessage}
                              setSelectedVisit={setSelectedVisit}
                              globalVisits={refetchVisits}
                              patient={infoData}
                              setHasChanged={setHasChanged}
                              hasChanged={hasChanged}
                              modalVisitChangeShow={modalVisitChangeShow}
                              setModalVisitChangeShow={setModalVisitChangeShow}
                              infoData={infoData}
                              selectedSessionId={selectedSessionId}
                              intro_date={selectedVisit?.intro_date}
                              intro_time={selectedVisit?.intro_time}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              </Stack>
            </Stack>
          </Box>
        }
      />
    </>
  );
};

export default AllBlocksAccordion;
